// Theme-aware solver loading modal. Every theme gets its own ritual while
// the route solver runs — the default keeps the classic rabbit-out-of-the-
// hat; each theme replaces both the animation AND what the work is called.
import React, { useState, useEffect } from 'react';
import { Dialog, Box, Typography } from '@mui/material';
import { useAppTheme } from '../../contexts/ThemeContext';

// ── Shared bits ──────────────────────────────────────────────────────

const mono = "'IBM Plex Mono', 'Courier New', Courier, monospace";

function Label({ children, sx }) {
  return (
    <Typography sx={{
      fontSize: '1.05rem',
      fontWeight: 600,
      letterSpacing: '0.12em',
      mt: 1,
      animation: 'solverTextPulse 1.5s ease-in-out infinite',
      '@keyframes solverTextPulse': {
        '0%, 100%': { opacity: 0.7 },
        '50%': { opacity: 1 },
      },
      ...sx,
    }}>
      {children}
    </Typography>
  );
}

// ── Default: the route draws itself through the gates ───────────────

function RouteAnim() {
  const gates = [[15, 95], [55, 30], [105, 75], [150, 25], [185, 85]];
  const delays = [0, 0.45, 0.85, 1.25, 1.7];
  return (
    <Box sx={{
      width: '200px', height: '120px', mx: 'auto',
      '@keyframes routeDraw': {
        '0%': { strokeDashoffset: 300 },
        '65%': { strokeDashoffset: 0 },
        '82%': { strokeDashoffset: 0 },
        '100%': { strokeDashoffset: 300 },
      },
      '@keyframes gateLight': {
        '0%': { fill: '#1e4bc3' },
        '5%': { fill: '#f9b612' },
        '62%': { fill: '#f9b612' },
        '78%': { fill: '#1e4bc3' },
        '100%': { fill: '#1e4bc3' },
      },
    }}>
      <svg width="200" height="120">
        <polyline
          points={gates.map((g) => g.join(',')).join(' ')}
          fill="none"
          stroke="#f9b612"
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
          strokeDasharray="300"
          style={{
            filter: 'drop-shadow(0 0 6px rgba(249, 182, 18, 0.5))',
            animation: 'routeDraw 2.6s ease-in-out infinite',
          }}
        />
        {gates.map(([x, y], i) => (
          <circle
            key={i}
            cx={x} cy={y} r="5"
            fill="#1e4bc3"
            stroke="#64b5f6"
            strokeWidth="1.5"
            style={{ animation: `gateLight 2.6s ease-in-out ${delays[i]}s infinite` }}
          />
        ))}
      </svg>
    </Box>
  );
}

// ── Apollo: the AGC at work — DSKY panel, COMP ACTY blinking ─────────

function DskyDigit({ values, period, delay }) {
  // flickers between candidate digits like the AGC registers updating
  const frames = {};
  values.forEach((v, i) => {
    frames[`${Math.round((i / values.length) * 100)}%`] = { content: `"${v}"` };
  });
  return (
    <Box component="span" sx={{
      '&::after': {
        content: `"${values[0]}"`,
        animation: `dskyFlick ${period}s steps(1) ${delay}s infinite`,
      },
      '@keyframes dskyFlick': frames,
    }} />
  );
}

function ApolloAnim() {
  return (
    <Box sx={{
      width: '230px', mx: 'auto', p: '14px 18px',
      background: '#0a0f0c', borderRadius: '4px',
      border: '1px solid #3a423c',
      boxShadow: 'inset 0 0 24px rgba(105, 212, 137, 0.08), 0 0 0 3px #14181a',
      fontFamily: mono, textAlign: 'left',
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box sx={{
          px: 1, py: 0.4, fontSize: '0.6rem', letterSpacing: '0.1em',
          color: '#0c1200', background: '#69d489', borderRadius: '2px', fontWeight: 700,
          animation: 'compActy 1.1s steps(1) infinite',
          '@keyframes compActy': {
            '0%': { opacity: 1 }, '12%': { opacity: 0.25 }, '22%': { opacity: 1 },
            '42%': { opacity: 1 }, '50%': { opacity: 0.25 }, '58%': { opacity: 1 },
            '74%': { opacity: 0.25 }, '82%': { opacity: 1 }, '100%': { opacity: 1 },
          },
        }}>
          COMP ACTY
        </Box>
        <Typography sx={{ color: '#9aa092', fontSize: '0.6rem', fontFamily: mono, letterSpacing: '0.12em' }}>
          PROG 63
        </Typography>
      </Box>
      {[
        ['VERB', ['1', '2', '1'], ['6', '1', '6']],
        ['NOUN', ['6', '9', '6'], ['8', '2', '8']],
        ['R1', ['+', '+', '+'], ['4', '7', '2'], ['2', '0', '8'], ['8', '1', '6'], ['1', '9', '4'], ['7', '5', '3']],
      ].map(([label, ...digitSets], row) => (
        <Box key={label} sx={{
          display: 'flex', alignItems: 'baseline', gap: 1.5,
          color: '#8ef0b6', fontSize: '1.1rem', fontFamily: mono,
          textShadow: '0 0 8px rgba(105, 212, 137, 0.5)',
          borderTop: row === 2 ? '1px solid rgba(105, 212, 137, 0.2)' : 'none',
          pt: row === 2 ? 0.5 : 0, mt: row === 2 ? 0.5 : 0,
        }}>
          <Typography sx={{ color: '#9aa092', fontSize: '0.6rem', fontFamily: mono, width: '34px', letterSpacing: '0.1em' }}>
            {label}
          </Typography>
          {digitSets.map((vals, i) => (
            <DskyDigit key={i} values={vals} period={0.9 + (i % 3) * 0.4} delay={i * 0.15} />
          ))}
        </Box>
      ))}
    </Box>
  );
}

// ── Blackbird: the teletype hammers out the plot ─────────────────────

function BlackbirdAnim() {
  return (
    <Box sx={{ width: '240px', mx: 'auto', fontFamily: mono, textAlign: 'left' }}>
      {[0, 1, 2].map((row) => (
        <Box key={row} sx={{ display: 'flex', alignItems: 'center', height: '20px' }}>
          <Box sx={{
            height: '10px',
            background: 'rgba(255, 176, 0, 0.55)',
            boxShadow: '0 0 8px rgba(255, 176, 0, 0.3)',
            animation: `ttyLine${row} ${2.4 + row * 0.5}s steps(${10 + row * 3}, jump-none) infinite`,
            [`@keyframes ttyLine${row}`]: {
              '0%': { width: '0%' },
              '70%': { width: `${78 - row * 14}%` },
              '100%': { width: `${78 - row * 14}%` },
            },
          }} />
          <Box sx={{
            width: '9px', height: '14px', ml: '3px',
            background: '#ffb000',
            animation: 'ttyCursor 0.5s steps(1) infinite',
            '@keyframes ttyCursor': { '0%': { opacity: 1 }, '50%': { opacity: 0 }, '100%': { opacity: 1 } },
          }} />
        </Box>
      ))}
      <Typography sx={{ color: 'rgba(255, 176, 0, 0.5)', fontSize: '0.6rem', fontFamily: mono, letterSpacing: '0.18em', mt: 1 }}>
        BIRDWATCH // ROUTING TRAFFIC
      </Typography>
    </Box>
  );
}

// ── Overwatch: brackets converge — the intercept resolves ────────────

function OverwatchAnim() {
  const bracket = (inset, dur, delay) => ({
    position: 'absolute', inset,
    background: `
      linear-gradient(to right, rgba(159,216,255,0.9) 11px, transparent 11px) top left / 100% 1.5px no-repeat,
      linear-gradient(to bottom, rgba(159,216,255,0.9) 11px, transparent 11px) top left / 1.5px 100% no-repeat,
      linear-gradient(to left, rgba(159,216,255,0.9) 11px, transparent 11px) top right / 100% 1.5px no-repeat,
      linear-gradient(to bottom, rgba(159,216,255,0.9) 11px, transparent 11px) top right / 1.5px 100% no-repeat,
      linear-gradient(to right, rgba(159,216,255,0.9) 11px, transparent 11px) bottom left / 100% 1.5px no-repeat,
      linear-gradient(to top, rgba(159,216,255,0.9) 11px, transparent 11px) bottom left / 1.5px 100% no-repeat,
      linear-gradient(to left, rgba(159,216,255,0.9) 11px, transparent 11px) bottom right / 100% 1.5px no-repeat,
      linear-gradient(to top, rgba(159,216,255,0.9) 11px, transparent 11px) bottom right / 1.5px 100% no-repeat`,
    animation: `owConverge ${dur}s ease-in-out ${delay}s infinite`,
  });
  return (
    <Box sx={{
      position: 'relative', width: '140px', height: '140px', mx: 'auto',
      '@keyframes owConverge': {
        '0%': { transform: 'scale(1.35)', opacity: 0 },
        '30%': { opacity: 0.9 },
        '55%': { transform: 'scale(1)', opacity: 0.9 },
        '85%': { transform: 'scale(1)', opacity: 0.9 },
        '100%': { transform: 'scale(0.95)', opacity: 0 },
      },
    }}>
      <Box sx={bracket(0, 2.4, 0)} />
      <Box sx={bracket('18px', 2.4, 0.5)} />
      <Box sx={{
        position: 'absolute', top: '50%', left: '50%', width: '5px', height: '5px',
        transform: 'translate(-50%, -50%)', borderRadius: '50%',
        background: '#9fd8ff', boxShadow: '0 0 10px rgba(159,216,255,0.9)',
        animation: 'owDot 1.2s ease-in-out infinite',
        '@keyframes owDot': { '0%, 100%': { opacity: 0.4 }, '50%': { opacity: 1 } },
      }} />
      <Box sx={{
        position: 'absolute', left: 0, right: 0, height: '2px',
        background: 'linear-gradient(90deg, transparent, rgba(159,216,255,0.5), transparent)',
        animation: 'owSweepY 1.8s linear infinite',
        '@keyframes owSweepY': { from: { top: 0 }, to: { top: '138px' } },
      }} />
    </Box>
  );
}

// ── Nightvision: target acquisition reticle ──────────────────────────

function NightvisionAnim() {
  return (
    <Box sx={{ position: 'relative', width: '130px', height: '130px', mx: 'auto' }}>
      <Box sx={{
        position: 'absolute', inset: 0, borderRadius: '50%',
        border: '1.5px solid rgba(0, 255, 65, 0.7)',
        borderTopColor: 'transparent',
        boxShadow: '0 0 14px rgba(0, 255, 65, 0.3)',
        animation: 'nvSpin 1.6s linear infinite',
        '@keyframes nvSpin': { to: { transform: 'rotate(360deg)' } },
      }} />
      <Box sx={{
        position: 'absolute', inset: '22px', borderRadius: '50%',
        border: '1px solid rgba(0, 255, 65, 0.4)',
        borderBottomColor: 'transparent',
        animation: 'nvSpinR 2.3s linear infinite',
        '@keyframes nvSpinR': { to: { transform: 'rotate(-360deg)' } },
      }} />
      <Box sx={{
        position: 'absolute', top: '50%', left: '50%', width: '54px', height: '54px',
        transform: 'translate(-50%, -50%)',
        background: `
          linear-gradient(0deg, transparent 47%, rgba(0,255,65,0.9) 50%, transparent 53%),
          linear-gradient(90deg, transparent 47%, rgba(0,255,65,0.9) 50%, transparent 53%)`,
        animation: 'nvPulse 1.2s ease-in-out infinite',
        '@keyframes nvPulse': { '0%, 100%': { opacity: 0.5 }, '50%': { opacity: 1 } },
      }} />
    </Box>
  );
}

// ── Submarine: active sonar — expanding ping rings ───────────────────

function SubmarineAnim() {
  return (
    <Box sx={{ position: 'relative', width: '140px', height: '140px', mx: 'auto' }}>
      {[0, 0.8, 1.6].map((delay, i) => (
        <Box key={i} sx={{
          position: 'absolute', top: '50%', left: '50%',
          width: '10px', height: '10px', borderRadius: '50%',
          border: '1.5px solid rgba(0, 255, 170, 0.8)',
          transform: 'translate(-50%, -50%)',
          animation: `ping 2.4s ease-out ${delay}s infinite`,
          '@keyframes ping': {
            '0%': { width: '10px', height: '10px', opacity: 0.9 },
            '100%': { width: '140px', height: '140px', opacity: 0 },
          },
        }} />
      ))}
      <Box sx={{
        position: 'absolute', top: '50%', left: '50%', width: '8px', height: '8px',
        transform: 'translate(-50%, -50%)', borderRadius: '50%',
        background: '#00FFAA', boxShadow: '0 0 12px rgba(0, 255, 170, 0.8)',
      }} />
    </Box>
  );
}

// ── Blade Runner: ENHANCE — the zoom-and-refocus ─────────────────────

function BladerunnerAnim() {
  return (
    <Box sx={{ position: 'relative', width: '150px', height: '110px', mx: 'auto', overflow: 'hidden' }}>
      <Box sx={{
        position: 'absolute', inset: 0,
        backgroundImage: `
          linear-gradient(0deg, rgba(255,153,0,0.25) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255,153,0,0.25) 1px, transparent 1px)`,
        backgroundSize: '15px 15px',
        animation: 'brZoom 2.6s steps(6, jump-none) infinite',
        '@keyframes brZoom': {
          '0%': { transform: 'scale(1)' },
          '70%': { transform: 'scale(1.9)' },
          '100%': { transform: 'scale(1.9)' },
        },
      }} />
      <Box sx={{
        position: 'absolute', top: '28px', left: '46px', width: '42px', height: '34px',
        border: '1.5px solid rgba(255, 224, 102, 0.9)',
        boxShadow: '0 0 14px rgba(255, 153, 0, 0.4)',
        animation: 'brBox 2.6s steps(6, jump-none) infinite',
        '@keyframes brBox': {
          '0%': { transform: 'scale(1)', opacity: 0.4 },
          '70%': { transform: 'scale(1.6)', opacity: 1 },
          '100%': { transform: 'scale(1.6)', opacity: 1 },
        },
      }} />
    </Box>
  );
}

// ── Orbital Command: a body in orbit ─────────────────────────────────

function OrbitAnim() {
  return (
    <Box sx={{ position: 'relative', width: '150px', height: '110px', mx: 'auto' }}>
      <Box sx={{
        position: 'absolute', top: '50%', left: '50%', width: '40px', height: '40px',
        transform: 'translate(-50%, -50%)', borderRadius: '50%',
        background: 'radial-gradient(circle at 35% 30%, #9dfdff, #0a4a55 70%)',
        boxShadow: '0 0 18px rgba(62, 245, 255, 0.4)',
      }} />
      <Box sx={{
        position: 'absolute', top: '50%', left: '50%', width: '130px', height: '54px',
        transform: 'translate(-50%, -50%) rotate(-18deg)',
        border: '1px solid rgba(62, 245, 255, 0.35)', borderRadius: '50%',
      }} />
      <Box sx={{
        position: 'absolute', top: '50%', left: '50%', width: '130px', height: '54px',
        transform: 'translate(-50%, -50%) rotate(-18deg)',
        animation: 'orbitSpin 2.2s linear infinite',
        '@keyframes orbitSpin': { to: { transform: 'translate(-50%, -50%) rotate(342deg)' } },
      }}>
        <Box sx={{
          position: 'absolute', top: '-3px', left: '50%', width: '7px', height: '7px',
          borderRadius: '50%', background: '#3ef5ff',
          boxShadow: '0 0 10px rgba(62, 245, 255, 0.9)',
        }} />
      </Box>
    </Box>
  );
}

// ── Cyberpunk: decryption glitch bars ────────────────────────────────

function CyberpunkAnim() {
  return (
    <Box sx={{ width: '200px', mx: 'auto' }}>
      {[0, 1, 2, 3, 4].map((i) => (
        <Box key={i} sx={{
          height: '10px', mb: '6px',
          background: i % 2 ? 'rgba(255, 0, 60, 0.65)' : 'rgba(0, 212, 255, 0.65)',
          boxShadow: i % 2 ? '0 0 10px rgba(255,0,60,0.4)' : '0 0 10px rgba(0,212,255,0.4)',
          animation: `glitch${i % 3} ${0.7 + i * 0.13}s steps(2, jump-none) infinite`,
          '@keyframes glitch0': {
            '0%': { width: '30%', marginLeft: '0%' },
            '50%': { width: '85%', marginLeft: '10%' },
            '100%': { width: '55%', marginLeft: '40%' },
          },
          '@keyframes glitch1': {
            '0%': { width: '70%', marginLeft: '25%' },
            '50%': { width: '40%', marginLeft: '0%' },
            '100%': { width: '90%', marginLeft: '5%' },
          },
          '@keyframes glitch2': {
            '0%': { width: '50%', marginLeft: '45%' },
            '50%': { width: '95%', marginLeft: '0%' },
            '100%': { width: '35%', marginLeft: '20%' },
          },
        }} />
      ))}
    </Box>
  );
}

// ── Graveyard Shift: a lantern gutters while the dead are raised ─────

function GraveyardAnim() {
  return (
    <Box sx={{ position: 'relative', width: '140px', height: '130px', mx: 'auto' }}>
      {/* the glow — breathing, occasionally faltering */}
      <Box sx={{
        position: 'absolute', top: '50%', left: '50%', width: '110px', height: '110px',
        transform: 'translate(-50%, -50%)', borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255, 214, 140, 0.30), rgba(255, 196, 107, 0.08) 55%, transparent 75%)',
        animation: 'gyLanternGlow 3.2s ease-in-out infinite',
        '@keyframes gyLanternGlow': {
          '0%, 100%': { opacity: 0.85, transform: 'translate(-50%, -50%) scale(1)' },
          '38%': { opacity: 1, transform: 'translate(-50%, -50%) scale(1.08)' },
          '46%': { opacity: 0.5, transform: 'translate(-50%, -50%) scale(0.94)' },
          '52%': { opacity: 0.95 },
          '74%': { opacity: 0.7 },
        },
      }} />
      {/* the flame */}
      <Box sx={{
        position: 'absolute', top: '50%', left: '50%', width: '16px', height: '26px',
        transform: 'translate(-50%, -60%)',
        borderRadius: '50% 50% 50% 50% / 62% 62% 38% 38%',
        background: 'radial-gradient(ellipse at 50% 75%, #fff3cf 0%, #ffc46b 55%, rgba(252, 120, 60, 0.5) 85%, transparent 100%)',
        filter: 'blur(0.5px)',
        transformOrigin: 'bottom center',
        animation: 'gyFlame 0.6s ease-in-out infinite alternate',
        '@keyframes gyFlame': {
          from: { transform: 'translate(-50%, -60%) scaleY(1) skewX(-2deg)' },
          to: { transform: 'translate(-50%, -60%) scaleY(1.18) skewX(3deg)' },
        },
      }} />
      {/* fog sliding under the light */}
      <Box sx={{
        position: 'absolute', bottom: '-6px', left: '-20px', right: '-20px', height: '36px',
        background: 'radial-gradient(ellipse 60% 100% at 50% 100%, rgba(184, 255, 217, 0.16), transparent 75%)',
        filter: 'blur(8px)',
        animation: 'gyFogSlide 5s ease-in-out infinite alternate',
        '@keyframes gyFogSlide': {
          from: { transform: 'translateX(-14px)' },
          to: { transform: 'translateX(14px)' },
        },
      }} />
    </Box>
  );
}

// ── Corporate: clean and professional ────────────────────────────────

function CorporateAnim() {
  return (
    <Box sx={{ position: 'relative', width: '70px', height: '70px', mx: 'auto' }}>
      <Box sx={{
        position: 'absolute', inset: 0, borderRadius: '50%',
        border: '4px solid #e3e8ef', borderTopColor: '#2563eb',
        animation: 'corpSpin 0.9s linear infinite',
        '@keyframes corpSpin': { to: { transform: 'rotate(360deg)' } },
      }} />
    </Box>
  );
}

// ── Theme registry ───────────────────────────────────────────────────

const SOLVER_THEMES = {
  default: {
    title: 'Autorouting…',
    Anim: RouteAnim,
    bar: '#F9B612',
    titleSx: { color: '#fff', letterSpacing: '0.05em', fontFamily: 'inherit' },
    paperSx: {
      backgroundColor: 'rgba(22, 26, 34, 0.95)',
      border: '1px solid rgba(249, 182, 18, 0.45)',
      boxShadow: '0 0 50px rgba(249, 182, 18, 0.15), 0 18px 50px rgba(0, 0, 0, 0.5)',
    },
  },
  apollo: {
    title: 'GUIDANCE COMPUTING…',
    Anim: ApolloAnim,
    bar: '#69d489',
    titleSx: { color: '#ece5d3', fontFamily: mono },
    paperSx: {
      backgroundColor: '#161a17',
      border: '1px solid #3a423c',
      boxShadow: '0 0 0 3px #14181a, 0 0 50px rgba(105, 212, 137, 0.12)',
      borderRadius: '6px',
    },
  },
  blackbird: {
    title: 'PLOTTING SORTIE…',
    Anim: BlackbirdAnim,
    titleSx: { color: '#ffb000', fontFamily: mono, textShadow: '0 0 10px rgba(255,176,0,0.4)' },
    paperSx: {
      backgroundColor: '#0c0800',
      border: '1px solid rgba(255, 176, 0, 0.5)',
      boxShadow: '0 0 40px rgba(255, 176, 0, 0.15)',
      borderRadius: 0,
    },
  },
  overwatch: {
    title: 'INTERCEPTING TRAFFIC…',
    Anim: OverwatchAnim,
    titleSx: { color: '#9fd8ff', fontFamily: mono },
    paperSx: {
      backgroundColor: 'rgba(10, 17, 26, 0.97)',
      border: '1px solid #1c2a3a',
      boxShadow: '0 18px 40px rgba(0, 0, 0, 0.5)',
      borderRadius: '2px',
    },
  },
  nightvision: {
    title: 'ACQUIRING TARGETS…',
    Anim: NightvisionAnim,
    titleSx: { color: '#00ff41', fontFamily: mono, textShadow: '0 0 10px rgba(0,255,65,0.5)' },
    paperSx: {
      backgroundColor: 'rgba(0, 20, 0, 0.95)',
      border: '1px solid rgba(0, 255, 65, 0.5)',
      boxShadow: '0 0 40px rgba(0, 255, 65, 0.2)',
    },
  },
  submarine: {
    title: 'PINGING CONTACTS…',
    Anim: SubmarineAnim,
    titleSx: { color: '#00FFAA', fontFamily: mono, textShadow: '0 0 10px rgba(0,255,170,0.4)' },
    paperSx: {
      backgroundColor: 'rgba(0, 20, 30, 0.95)',
      border: '1px solid rgba(0, 255, 170, 0.45)',
      boxShadow: '0 0 40px rgba(0, 255, 170, 0.15)',
    },
  },
  bladerunner: {
    title: 'ENHANCING…',
    Anim: BladerunnerAnim,
    titleSx: { color: '#ffe066', fontFamily: mono, textShadow: '0 0 10px rgba(255,153,0,0.4)' },
    paperSx: {
      backgroundColor: 'rgba(20, 10, 0, 0.95)',
      border: '1px solid rgba(255, 153, 0, 0.45)',
      boxShadow: '0 0 40px rgba(255, 153, 0, 0.15)',
    },
  },
  orbit: {
    title: 'COMPUTING ORBITS…',
    Anim: OrbitAnim,
    titleSx: { color: '#3ef5ff', fontFamily: mono, textShadow: '0 0 10px rgba(62,245,255,0.4)' },
    paperSx: {
      backgroundColor: 'rgba(2, 11, 22, 0.95)',
      border: '1px solid rgba(62, 245, 255, 0.4)',
      boxShadow: '0 0 40px rgba(62, 245, 255, 0.15)',
    },
  },
  cyberpunk: {
    title: 'DECRYPTING ROUTES…',
    Anim: CyberpunkAnim,
    titleSx: { color: '#00d4ff', fontFamily: mono, textShadow: '0 0 10px rgba(0,212,255,0.5)' },
    paperSx: {
      backgroundColor: 'rgba(10, 8, 14, 0.96)',
      border: '1px solid rgba(0, 212, 255, 0.45)',
      boxShadow: '0 0 40px rgba(0, 212, 255, 0.2), 0 0 80px rgba(255, 0, 60, 0.1)',
    },
  },
  graveyard: {
    title: 'WORKING THE GRAVEYARD…',
    Anim: GraveyardAnim,
    bar: '#b8ffd9',
    titleSx: { color: '#e8e6da', fontFamily: "'Georgia', 'Times New Roman', serif", textShadow: '0 0 12px rgba(184, 255, 217, 0.3)' },
    paperSx: {
      backgroundColor: 'rgba(14, 19, 16, 0.97)',
      border: '1px solid rgba(140, 180, 150, 0.4)',
      boxShadow: '0 0 50px rgba(184, 255, 217, 0.1), 0 24px 60px rgba(0, 0, 0, 0.6)',
      borderRadius: '6px',
    },
  },
  corporate: {
    title: 'Optimizing routes…',
    Anim: CorporateAnim,
    bar: '#2563eb',
    titleSx: { color: '#243240', letterSpacing: '0.02em', fontWeight: 600 },
    paperSx: {
      backgroundColor: '#ffffff',
      border: '1px solid #e3e8ef',
      boxShadow: '0 8px 30px rgba(16, 24, 40, 0.12)',
    },
  },
};

// Progress bar — there are no real progress events from a single-shot
// solve, so this is HONEST about that: it climbs fast then eases toward a
// 92% asymptote and never completes on its own (only the modal closing,
// when the solve actually returns, "finishes" it). A continuous sheen
// sweeps the fill so it always reads "alive" even once the climb plateaus.
function ProgressBar({ pct, color }) {
  return (
    <Box sx={{
      width: '230px', height: 4, mt: 0.5,
      borderRadius: 2, overflow: 'hidden',
      backgroundColor: 'rgba(140,140,140,0.22)',
    }}>
      <Box sx={{
        height: '100%', width: `${pct}%`,
        backgroundColor: color, borderRadius: 2,
        transition: 'width 0.2s linear',
        position: 'relative', overflow: 'hidden',
      }}>
        <Box sx={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent)',
          animation: 'solverBarSheen 1.2s ease-in-out infinite',
          '@keyframes solverBarSheen': {
            '0%': { transform: 'translateX(-100%)' },
            '100%': { transform: 'translateX(220%)' },
          },
        }} />
      </Box>
    </Box>
  );
}

function SolverLoadingModal({ open }) {
  const { currentTheme } = useAppTheme();
  const cfg = SOLVER_THEMES[currentTheme] || SOLVER_THEMES.default;
  const { Anim } = cfg;
  const barColor = cfg.bar || (cfg.titleSx && cfg.titleSx.color) || '#F9B612';

  // Elapsed seconds, ticking only while open — drives the asymptotic fill
  // and the "running extra passes" reassurance on the rare long solve.
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!open) { setElapsed(0); return undefined; }
    const start = Date.now();
    const id = setInterval(() => setElapsed((Date.now() - start) / 1000), 120);
    return () => clearInterval(id);
  }, [open]);

  const pct = 92 * (1 - Math.exp(-elapsed / 3.2));

  return (
    <Dialog
      open={open}
      PaperProps={{
        sx: {
          borderRadius: '20px',
          overflow: 'visible',
          minWidth: '320px',
          textAlign: 'center',
          padding: '40px 48px',
          backdropFilter: 'blur(20px)',
          ...cfg.paperSx,
        },
      }}
      slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.7)' } } }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        <Anim />
        <Label sx={cfg.titleSx}>{cfg.title}</Label>
        <ProgressBar pct={pct} color={barColor} />
        {elapsed > 14 && (
          <Typography sx={{
            fontSize: '0.72rem', mt: -0.5, opacity: 0.6, fontWeight: 400,
            color: (cfg.titleSx && cfg.titleSx.color) || '#fff',
            fontFamily: (cfg.titleSx && cfg.titleSx.fontFamily) || 'inherit',
          }}>
            Complex routing — running extra passes…
          </Typography>
        )}
      </Box>
    </Dialog>
  );
}

export default SolverLoadingModal;
